import React, { useCallback, useEffect, useRef, useState } from 'react';
import { io } from 'socket.io-client';
import { Device } from 'mediasoup-client';
import { Box, Card, CardContent, Typography, Button } from '@mui/material';
import { useAuth } from '../../contexts/AuthContext';
import { useAgent } from '../../contexts/AgentContext';
import { useSoftphone } from '../../contexts/SoftphoneContext';

const getBaseUrl = (apiBaseUrl) => {
  if (!apiBaseUrl) return window.location.origin;
  try {
    if (apiBaseUrl.startsWith('/')) return window.location.origin;
    const parsed = new URL(apiBaseUrl);
    const port = parsed.port ? `:${parsed.port}` : '';
    return `${parsed.protocol}//${parsed.hostname}${port}`;
  } catch {
    return window.location.origin;
  }
};

export default function SoftphoneSfu() {
  const { token, user } = useAuth();
  const { agentStatus, previewSession } = useAgent();
  const {
    setStatus,
    setInCall,
    setCallData,
    setSoftphoneFunctions,
    setHasActiveRtp,
    setMuted: setContextMuted,
    setRegistered,
    setTypificationData,
    callData,
    addCall,
    removeCall,
    holdCallState,
    unholdCallState,
    setCurrentCall,
    mergeCallsState,
    addConferenceParticipant,
    removeConferenceParticipant,
    clearAllCalls,
    calls
  } = useSoftphone();

  const [callId, setCallId] = useState(null);
  const [callerId, setCallerId] = useState('');
  const [connected, setConnected] = useState(false);
  const socketRef = useRef(null);
  const connectedOnceRef = useRef(false);
  const reconnectTimerRef = useRef(null);
  const callIdRef = useRef(null);
  const actionIdRef = useRef(null);
  const tokenRef = useRef(token);
  const deviceRef = useRef(null);
  const sendTransportRef = useRef(null);
  const recvTransportRef = useRef(null);
  const producerRef = useRef(null);
  const remoteAudioRef = useRef(null);
  const audioCtxRef = useRef(null);
  const ringCtxRef = useRef(null);
  const ringGainRef = useRef(null);
  const ringOscsRef = useRef([]);
  const ringTimersRef = useRef({ interval: null, off: null });
  const ringActiveRef = useRef(false);
  const consumedRef = useRef(new Set());
  const mutedRef = useRef(false);
  const functionsSetRef = useRef(false);
  const joinAttemptRef = useRef(0);
  const setStatusRef = useRef(setStatus);
  const setRegisteredRef = useRef(setRegistered);
  const setCallDataRef = useRef(setCallData);
  const setInCallRef = useRef(setInCall);
  const setHasActiveRtpRef = useRef(setHasActiveRtp);
  const setContextMutedRef = useRef(setContextMuted);
  const setTypificationDataRef = useRef(setTypificationData);
  const skillDataFetchedRef = useRef(false);
  const agentStatusRef = useRef(agentStatus);
  const uniqueIdMapRef = useRef(new Map());
  const pendingUniqueIdRef = useRef(null);
  const metaRetryRef = useRef(new Map());
  const skillDataRetryRef = useRef(new Map());
  const joiningRef = useRef(false);
  const recoveryAttemptedRef = useRef(false);
  const recoveryInFlightRef = useRef(false);
  const callMediaRef = useRef(new Map());  // callId -> { sendTransport, recvTransport, producer, consumed, consumers, clonedTrack }
  const micTrackRef = useRef(null);         // shared mic MediaStreamTrack (reused across calls)
  const processedManualCallsRef = useRef(new Set());  // actionIds already processed by manual call useEffect
  const previewFetchControllerRef = useRef(null);
  const callDataRef = useRef(callData);

  const baseUrl = getBaseUrl(process.env.REACT_APP_API_BASE_URL);
  const wsBaseUrl = (process.env.REACT_APP_WEBSOCKET_URL || baseUrl || '').trim() || window.location.origin;

  useEffect(() => {
    callIdRef.current = callId;
  }, [callId]);

  useEffect(() => {
    tokenRef.current = token;
  }, [token]);

  useEffect(() => {
    callDataRef.current = callData;
  }, [callData]);

  useEffect(() => {
    agentStatusRef.current = agentStatus;
  }, [agentStatus]);

  useEffect(() => {
    setStatusRef.current = setStatus;
    setRegisteredRef.current = setRegistered;
    setCallDataRef.current = setCallData;
    setInCallRef.current = setInCall;
    setHasActiveRtpRef.current = setHasActiveRtp;
    setContextMutedRef.current = setContextMuted;
    setTypificationDataRef.current = setTypificationData;
  }, [setStatus, setRegistered, setCallData, setInCall, setHasActiveRtp, setContextMuted, setTypificationData]);

  // Handle preview sessions (PREVIEW_DATA arrives before manual dialing in preview mode)
  useEffect(() => {
    if (!previewSession) {
      if (previewFetchControllerRef.current) {
        previewFetchControllerRef.current.abort();
        previewFetchControllerRef.current = null;
      }
      return;
    }

    const { skill, customerData, previewId } = previewSession;
    const dataId = customerData?.id || customerData?.data_id || customerData?.dataId || null;
    const phoneNumber =
      customerData?.phone?.number ||
      customerData?.phones?.number1 ||
      customerData?.Telefono1 ||
      customerData?.telefono ||
      '';

    const fallbackCustomerData = { ...(customerData || {}) };
    const flattenedCustomFields =
      fallbackCustomerData?.customFields && typeof fallbackCustomerData.customFields === 'object'
        ? fallbackCustomerData.customFields
        : {};
    if (phoneNumber && !fallbackCustomerData.Telefono1) {
      fallbackCustomerData.Telefono1 = phoneNumber;
    }

    const fallbackSkillDataRow = {
      ...(fallbackCustomerData || {}),
      ...(flattenedCustomFields || {})
    };
    const fallbackFieldNames = Object.keys(fallbackSkillDataRow || {});
    const fallbackPhoneFields = phoneNumber ? [{ name: 'Telefono1', number_mapping: 1 }] : [];
    const fallbackSkillData = {
      skillData: fallbackSkillDataRow,
      datastructureFields: fallbackFieldNames,
      phoneFields: fallbackPhoneFields
    };

    const basePreviewData = {
      source: 'preview',
      dialingMode: 'Preview',
      dialingModeNormalized: 'preview',
      previewId,
      skill: skill || null,
      data_id: dataId,
      callNumber: phoneNumber,
      phone_number: phoneNumber,
      previewData: previewSession,
      skillData: fallbackSkillData,
      timestamp: new Date().toISOString()
    };

    console.warn('[PREVIEW TRACE] SoftphoneSfu received previewSession', {
      previewId,
      skill,
      dataId,
      hasCustomerData: !!customerData
    });

    setCallDataRef.current(basePreviewData);

    if (!skill || !dataId || !tokenRef.current) {
      console.warn('[PREVIEW TRACE] SoftphoneSfu preview fetch skipped', {
        hasSkill: !!skill,
        hasDataId: !!dataId,
        hasToken: !!tokenRef.current,
        previewId
      });
      return;
    }

    const controller = new AbortController();
    previewFetchControllerRef.current = controller;

    (async () => {
      try {
        const response = await fetch(`/api/admin/softphone/skill-data/${encodeURIComponent(skill)}/${encodeURIComponent(dataId)}`, {
          headers: {
            Authorization: `Bearer ${tokenRef.current}`,
            'Content-Type': 'application/json'
          },
          signal: controller.signal
        });

        if (!response.ok) {
          console.warn('[PREVIEW] SoftphoneSfu skill-data fetch failed', response.status, response.statusText);
          return;
        }

        const result = await response.json();
        const skillData = result?.data ?? result;
        console.warn('[PREVIEW TRACE] SoftphoneSfu skill-data fetched', {
          previewId,
          hasSkillData: !!skillData,
          keys: skillData ? Object.keys(skillData) : []
        });

        const current = callDataRef.current;
        if (current && current.previewId === previewId) {
          setCallDataRef.current({
            ...current,
            skillData
          });
        }
      } catch (error) {
        if (controller.signal.aborted) return;
        console.error('[PREVIEW] Error fetching skill data in SoftphoneSfu:', error);
      } finally {
        if (previewFetchControllerRef.current === controller) {
          previewFetchControllerRef.current = null;
        }
      }
    })();

    return () => {
      controller.abort();
      if (previewFetchControllerRef.current === controller) {
        previewFetchControllerRef.current = null;
      }
    };
  }, [previewSession]);

  const apiCall = useCallback(async (path, opts = {}) => {
    const res = await fetch(`/api/admin${path}`, {
      ...opts,
      headers: {
        Authorization: `Bearer ${tokenRef.current}`,
        'Content-Type': 'application/json',
        ...(opts.headers || {})
      }
    });
    const json = await res.json();
    if (!res.ok || json.success === false) {
      throw new Error(json.message || 'Request failed');
    }
    return json.data ?? json;
  }, [token]);

  const looksLikeActionId = useCallback((id) => {
    if (!id) return false;
    const idStr = String(id);
    return idStr.includes('_');
  }, []);

  const looksLikeUniqueId = useCallback((id) => {
    if (!id) return false;
    const idStr = String(id);
    return /^[0-9]+\.[0-9]+$/.test(idStr);
  }, []);

  const ensureDevice = useCallback(async (callIdParam) => {
    if (deviceRef.current) return deviceRef.current;
    const rtpCapabilities = await apiCall(`/sfu/rooms/${callIdParam}/rtp-capabilities`);
    const device = new Device();
    await device.load({ routerRtpCapabilities: rtpCapabilities });
    deviceRef.current = device;
    return device;
  }, [apiCall]);

  const consumeProducer = useCallback(async (callIdParam, producerId) => {
    if (consumedRef.current.has(producerId)) return;
    const device = deviceRef.current;
    const recvTransport = recvTransportRef.current;
    if (!device || !recvTransport) return;

    let result;
    try {
      result = await apiCall(`/sfu/rooms/${callIdParam}/consume`, {
        method: 'POST',
        body: JSON.stringify({
          transportId: recvTransport.id,
          producerId,
          rtpCapabilities: device.rtpCapabilities
        })
      });
    } catch (err) {
      const message = err?.message || String(err);
      if (message.includes('Producer') || message.includes('Transport')) {
        setTimeout(() => consumeProducer(callIdParam, producerId), 500);
        return;
      }
      throw err;
    }

    const consumer = await recvTransport.consume({
      id: result.id,
      producerId: result.producerId,
      kind: result.kind,
      rtpParameters: result.rtpParameters
    });

    consumedRef.current.add(producerId);
    // Store consumer in per-call media map for multi-call audio switching
    const mediaEntry = callMediaRef.current.get(callIdParam);
    if (mediaEntry) {
      if (!mediaEntry.consumers) mediaEntry.consumers = [];
      mediaEntry.consumers.push(consumer);
    }
    const stream = new MediaStream();
    stream.addTrack(consumer.track);
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = stream;
      await remoteAudioRef.current.play().catch(() => {});
    }
  }, [apiCall]);

  const stopRing = useCallback(() => {
    ringActiveRef.current = false;
    if (ringTimersRef.current.interval) {
      clearInterval(ringTimersRef.current.interval);
      ringTimersRef.current.interval = null;
    }
    if (ringTimersRef.current.off) {
      clearTimeout(ringTimersRef.current.off);
      ringTimersRef.current.off = null;
    }
    try {
      if (ringGainRef.current) {
        ringGainRef.current.gain.setValueAtTime(0, ringCtxRef.current?.currentTime || 0);
      }
    } catch {}
    try {
      ringOscsRef.current.forEach((osc) => {
        try { osc.stop(); } catch {}
        try { osc.disconnect(); } catch {}
      });
    } catch {}
    ringOscsRef.current = [];
    try { ringGainRef.current?.disconnect(); } catch {}
    ringGainRef.current = null;
    try { ringCtxRef.current?.close(); } catch {}
    ringCtxRef.current = null;
  }, []);

  const resetMuteState = useCallback(() => {
    mutedRef.current = false;
    setContextMutedRef.current(false);
  }, []);

  const startRing = useCallback(async () => {
    if (ringActiveRef.current) return;
    ringActiveRef.current = true;
    try {
      const AudioCtx = window.AudioContext || window.webkitAudioContext;
      if (!AudioCtx) return;
      if (!ringCtxRef.current) {
        ringCtxRef.current = new AudioCtx();
      }
      if (ringCtxRef.current.state === 'suspended') {
        await ringCtxRef.current.resume().catch(() => {});
      }
      if (!ringGainRef.current) {
        const gain = ringCtxRef.current.createGain();
        gain.gain.value = 0;
        gain.connect(ringCtxRef.current.destination);
        ringGainRef.current = gain;
      }
      if (!ringOscsRef.current.length) {
        const osc1 = ringCtxRef.current.createOscillator();
        const osc2 = ringCtxRef.current.createOscillator();
        osc1.frequency.value = 440;
        osc2.frequency.value = 480;
        osc1.connect(ringGainRef.current);
        osc2.connect(ringGainRef.current);
        osc1.start();
        osc2.start();
        ringOscsRef.current = [osc1, osc2];
      }

      const ringOnce = () => {
        if (!ringGainRef.current || !ringCtxRef.current) return;
        ringGainRef.current.gain.setValueAtTime(0.2, ringCtxRef.current.currentTime);
        if (ringTimersRef.current.off) clearTimeout(ringTimersRef.current.off);
        ringTimersRef.current.off = setTimeout(() => {
          if (ringGainRef.current && ringCtxRef.current) {
            ringGainRef.current.gain.setValueAtTime(0, ringCtxRef.current.currentTime);
          }
        }, 2000);
      };

      ringOnce();
      ringTimersRef.current.interval = setInterval(ringOnce, 6000);
    } catch (err) {
      console.warn('[SOFTPHONE] Failed to start ring tone:', err?.message || err);
      stopRing();
    }
  }, [stopRing]);

  const getAudioContext = useCallback(() => {
    try {
      if (!audioCtxRef.current) {
        const Ctx = window.AudioContext || window.webkitAudioContext;
        if (!Ctx) return null;
        audioCtxRef.current = new Ctx();
      }
      if (audioCtxRef.current.state === 'suspended') {
        audioCtxRef.current.resume().catch(() => {});
      }
      return audioCtxRef.current;
    } catch {
      return null;
    }
  }, []);

  const playIncomingBeep = useCallback(async () => {
    try {
      const ctx = getAudioContext();
      if (!ctx) return;

      const osc = ctx.createOscillator();
      const gain = ctx.createGain();

      osc.type = 'sine';
      osc.frequency.setValueAtTime(880, ctx.currentTime);

      gain.gain.setValueAtTime(0.0001, ctx.currentTime);
      gain.gain.exponentialRampToValueAtTime(0.25, ctx.currentTime + 0.01);
      gain.gain.exponentialRampToValueAtTime(0.0001, ctx.currentTime + 0.3);

      osc.connect(gain).connect(ctx.destination);
      osc.start();
      osc.stop(ctx.currentTime + 0.35);

      osc.onended = () => {
        try {
          osc.disconnect();
          gain.disconnect();
        } catch {}
      };
    } catch (err) {
      console.warn('[SOFTPHONE] Error reproduciendo beep de llamada entrante:', err?.message || err);
    }
  }, [getAudioContext]);

  const playCallEndBeep = useCallback(async () => {
    try {
      const ctx = getAudioContext();
      if (!ctx) return;

      const playBeep = (startAt, duration) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();

        osc.type = 'sine';
        osc.frequency.setValueAtTime(300, startAt);

        gain.gain.setValueAtTime(0.0001, startAt);
        gain.gain.exponentialRampToValueAtTime(0.18, startAt + 0.02);
        gain.gain.exponentialRampToValueAtTime(0.0001, startAt + duration);

        osc.connect(gain).connect(ctx.destination);
        osc.start(startAt);
        osc.stop(startAt + duration + 0.02);

        osc.onended = () => {
          try {
            osc.disconnect();
            gain.disconnect();
          } catch {}
        };
      };

      const t0 = ctx.currentTime;
      playBeep(t0, 0.18);
      playBeep(t0 + 0.33, 0.18);
    } catch (err) {
      console.warn('[SOFTPHONE] Error reproduciendo beep de fin de llamada:', err?.message || err);
    }
  }, [getAudioContext]);

  useEffect(() => {
    const actionId = callData?.actionId || callData?.action_id || callData?.actionid || null;
    if (actionId) actionIdRef.current = actionId;

    // For manual calls (from ClientDataPanel), there's no call:incoming event.
    // Set callIdRef from callData so hangup and SFU join work.
    if (callData?.source === 'manual' && actionId && String(actionId).includes('_')) {
      // Si ya procesamos este actionId, skip (evita re-trigger después de cleanupMedia)
      if (processedManualCallsRef.current.has(actionId)) return;
      // Si ya estamos trackeando este callId, skip
      if (callIdRef.current && String(callIdRef.current) === String(actionId)) return;
      processedManualCallsRef.current.add(actionId);

      console.log('[SOFTPHONE] Manual call detected, actionId:', actionId, 'existing callId:', callIdRef.current);

      // Si hay una llamada activa diferente, hold inline (no puede usar holdCallMedia aún)
      const existingCallId = callIdRef.current;
      if (existingCallId && callMediaRef.current.has(existingCallId)) {
        console.log('[SOFTPHONE] Holding existing call for second manual call:', existingCallId);
        const media = callMediaRef.current.get(existingCallId);
        if (media?.producer && !media.producer.paused) {
          try { media.producer.pause(); } catch (e) { console.warn('[SOFTPHONE] pause err', e); }
        }
        sendTransportRef.current = null;
        recvTransportRef.current = null;
        producerRef.current = null;
        consumedRef.current = new Set();
        if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
        holdCallState(existingCallId);
        socketRef.current?.emit('call:hold', { callId: existingCallId });
      }

      resetMuteState();
      setCallId(actionId);
      callIdRef.current = actionId;
      setInCallRef.current(true);
      setStatusRef.current('Marcando');
      startRing();
      // Registrar en el estado multi-call para que currentCallId se setee
      addCall(actionId, {
        actionId,
        skill: callData?.skill || null,
        data_id: callData?.data_id || null,
        callNumber: callData?.callNumber || callData?.phone_number || callData?.phone || '',
        phone_number: callData?.phone_number || callData?.phone || '',
        source: 'manual',
        timestamp: new Date().toISOString()
      });
    }
  }, [callData, startRing, resetMuteState, addCall, holdCallState]);

  const cleanupMedia = useCallback(() => {
    // Close all per-call media from the multi-call map
    for (const [, media] of callMediaRef.current) {
      try { media.clonedTrack?.stop(); } catch {}
      try { media.producer?.close(); } catch {}
      if (media.consumers) {
        media.consumers.forEach(c => { try { c.close(); } catch {} });
      }
      try { media.sendTransport?.close(); } catch {}
      try { media.recvTransport?.close(); } catch {}
    }
    callMediaRef.current.clear();
    // Stop shared mic track
    try { micTrackRef.current?.stop(); } catch {}
    micTrackRef.current = null;
    // Stop local audio track so the mic is released
    if (producerRef.current) {
      try { producerRef.current.track?.stop(); } catch {}
      try { producerRef.current.close(); } catch {}
    }
    try { sendTransportRef.current?.close(); } catch {}
    try { recvTransportRef.current?.close(); } catch {}
    sendTransportRef.current = null;
    recvTransportRef.current = null;
    producerRef.current = null;
    consumedRef.current = new Set();
    joinAttemptRef.current = 0;
    joiningRef.current = false;
    if (remoteAudioRef.current) {
      remoteAudioRef.current.srcObject = null;
    }
    stopRing();
    resetMuteState();
    playCallEndBeep();
    setCallId(null);
    callIdRef.current = null;
    actionIdRef.current = null;
    skillDataFetchedRef.current = false;
    setHasActiveRtpRef.current(false);
    setCallerId('');
    recoveryAttemptedRef.current = false;
    recoveryInFlightRef.current = false;
    // Limpiar estado multi-call del contexto
    clearAllCalls();
  }, [stopRing, resetMuteState, playCallEndBeep, clearAllCalls]);

  // Fetch call metadata + skillData by ACTION_ID, then push to context
  const fetchCallMetadata = useCallback(async (actionId) => {
    if (!actionId || skillDataFetchedRef.current) {
      if (!actionId) {
        console.warn('[SOFTPHONE][DBG] fetchCallMetadata skipped: no actionId');
      } else {
        console.warn('[SOFTPHONE][DBG] fetchCallMetadata skipped: skillDataFetchedRef already true', { actionId });
      }
      return;
    }
    try {
      const meta = await apiCall(`/softphone/call-metadata/${encodeURIComponent(actionId)}`);
      if (!meta) return;
      skillDataFetchedRef.current = true;
      metaRetryRef.current.delete(actionId);

      const callMetadata = {
        actionId: meta.actionId || actionId,
        skill: meta.skill,
        data_id: meta.data_id,
        callNumber: meta.phone_number,
        phone_number: meta.phone_number,
        idlog_calls: meta.idlog_calls,
        idload: meta.idload,
        skillData: meta.skillData || null,
        source: 'predictive',
        timestamp: new Date().toISOString()
      };
      console.log('[SOFTPHONE][DBG] Call metadata fetched keys:', {
        actionId,
        keys: Object.keys(meta || {}),
        hasSkillData: !!meta?.skillData,
        hasSkillConfig: !!meta?.skillData?.skillConfig
      });
      console.log('[SOFTPHONE] Call metadata fetched:', callMetadata);
      setCallDataRef.current(callMetadata);

      if (meta.skill && meta.data_id) {
        setTypificationDataRef.current({
          skillName: meta.skill,
          dataId: meta.data_id,
          actionId: meta.actionId || actionId,
          callNumber: meta.phone_number,
          timestamp: new Date().toISOString(),
          source: 'incoming'
        });
      }
    } catch (err) {
      console.warn('[SOFTPHONE] Failed to fetch call metadata:', err?.message || err);
      console.warn('[SOFTPHONE][DBG] call-metadata error', { actionId, error: err?.message || String(err) });
      const retries = metaRetryRef.current.get(actionId) || 0;
      if (retries < 5 && callIdRef.current) {
        metaRetryRef.current.set(actionId, retries + 1);
        setTimeout(() => {
          if (callIdRef.current) fetchCallMetadata(actionId);
        }, 1000);
      }
    }
  }, [apiCall]);

  const scheduleSkillDataRetry = useCallback((params) => {
    const { callId, skill, dataId, actionId, phoneNumber, payload } = params || {};
    if (!callId || !skill || !dataId) return;
    const key = String(callId);
    const attempts = skillDataRetryRef.current.get(key) || 0;
    if (attempts >= 3) return;

    const delay = attempts === 0 ? 400 : attempts === 1 ? 1200 : 2500;
    skillDataRetryRef.current.set(key, attempts + 1);
    setTimeout(async () => {
      if (!callIdRef.current || String(callIdRef.current) !== String(callId)) return;
      if (skillDataFetchedRef.current) return;
      try {
        const skillData = await apiCall(`/softphone/skill-data/${encodeURIComponent(skill)}/${encodeURIComponent(dataId)}`);
        skillDataFetchedRef.current = true;
        const callMetadata = {
          actionId,
          skill,
          data_id: dataId,
          callNumber: phoneNumber,
          phone_number: phoneNumber,
          idlog_calls: payload?.idlog_calls,
          idload: payload?.idload,
          skillData: skillData || null,
          source: 'predictive',
          timestamp: new Date().toISOString()
        };
        console.log('[SOFTPHONE] skillData retry success:', callMetadata);
        setCallDataRef.current(callMetadata);
        setTypificationDataRef.current({
          skillName: skill,
          dataId,
          actionId,
          callNumber: phoneNumber,
          timestamp: new Date().toISOString(),
          source: 'incoming'
        });
      } catch (err) {
        console.warn('[SOFTPHONE] skillData retry failed:', err?.message || err);
        scheduleSkillDataRetry(params);
      }
    }, delay);
  }, [apiCall]);

  const createWebRtcTransport = useCallback(async (callIdParam) => {
    const transportInfo = await apiCall(`/sfu/rooms/${callIdParam}/transport`, { method: 'POST' });
    return transportInfo;
  }, [apiCall]);

  const joinSfu = useCallback(async (callIdParam) => {
    if (!callIdParam || !tokenRef.current) return;
    if (callMediaRef.current.has(callIdParam)) return; // Already joined this room
    if (sendTransportRef.current || recvTransportRef.current) return;
    if (joiningRef.current) return;
    joiningRef.current = true;

    try {
      const device = await ensureDevice(callIdParam);

      const sendInfo = await createWebRtcTransport(callIdParam);
      const recvInfo = await createWebRtcTransport(callIdParam);

      const sendTransport = device.createSendTransport(sendInfo);
      const recvTransport = device.createRecvTransport(recvInfo);

      sendTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          await apiCall(`/sfu/rooms/${callIdParam}/transport/${sendTransport.id}/connect`, {
            method: 'POST',
            body: JSON.stringify({ dtlsParameters })
          });
          callback();
        } catch (err) {
          errback(err);
        }
      });

      recvTransport.on('connect', async ({ dtlsParameters }, callback, errback) => {
        try {
          await apiCall(`/sfu/rooms/${callIdParam}/transport/${recvTransport.id}/connect`, {
            method: 'POST',
            body: JSON.stringify({ dtlsParameters })
          });
          callback();
        } catch (err) {
          errback(err);
        }
      });

      sendTransport.on('produce', async ({ kind, rtpParameters, appData }, callback, errback) => {
        try {
          const result = await apiCall(`/sfu/rooms/${callIdParam}/produce`, {
            method: 'POST',
            body: JSON.stringify({ transportId: sendTransport.id, kind, rtpParameters, appData })
          });
          callback({ id: result.id });
        } catch (err) {
          errback(err);
        }
      });

      sendTransportRef.current = sendTransport;
      recvTransportRef.current = recvTransport;

      // Reuse mic track if available (multi-call), clone for each room
      let micTrack = micTrackRef.current;
      if (!micTrack || micTrack.readyState === 'ended') {
        const localStream = await navigator.mediaDevices.getUserMedia({ audio: true, video: false });
        micTrack = localStream.getAudioTracks()[0];
        micTrackRef.current = micTrack;
      }
      const clonedTrack = micTrack.clone();
      producerRef.current = await sendTransport.produce({
        track: clonedTrack,
        appData: { role: 'agent', broadcastToAsterisk: true }
      });

      // Store per-call media BEFORE consuming (so consumeProducer can store consumers)
      callMediaRef.current.set(callIdParam, {
        sendTransport,
        recvTransport,
        producer: producerRef.current,
        consumed: new Set(),
        consumers: [],
        clonedTrack
      });

      const producers = await apiCall(`/sfu/rooms/${callIdParam}/producers`);
      for (const producer of producers) {
        if (producer.id === producerRef.current.id) continue;
        await consumeProducer(callIdParam, producer.id);
      }

      // Sync consumed set to per-call entry
      const joinedEntry = callMediaRef.current.get(callIdParam);
      if (joinedEntry) joinedEntry.consumed = new Set(consumedRef.current);

      setHasActiveRtpRef.current(true);
      resetMuteState();
      stopRing();
    } catch (err) {
      const message = err?.message || String(err);
      console.warn('[SOFTPHONE][SFU] joinSfu error:', message);
      if (message.includes('Transport') && message.includes('not found')) {
        joinAttemptRef.current += 1;
        if (joinAttemptRef.current <= 1) {
          try {
            sendTransportRef.current?.close?.();
            recvTransportRef.current?.close?.();
          } catch {}
          sendTransportRef.current = null;
          recvTransportRef.current = null;
          producerRef.current = null;
          consumedRef.current = new Set();
          joiningRef.current = false; // Reset lock before retry
          await joinSfu(callIdParam);
          return; // Skip finally (recursive call handles its own lock)
        }
      }
      // Room no longer exists (call ended before we could join) — cleanup instead of getting stuck
      if (message.includes('Room') && message.includes('not found') || message.includes('404') || message.includes('not found')) {
        console.warn('[SOFTPHONE][SFU] Room not found, call likely ended. Cleaning up.');
        try {
          sendTransportRef.current?.close?.();
          recvTransportRef.current?.close?.();
        } catch {}
        sendTransportRef.current = null;
        recvTransportRef.current = null;
        producerRef.current = null;
        consumedRef.current = new Set();
        callIdRef.current = null;
        actionIdRef.current = null;
        setCallId(null);
        setInCallRef.current(false);
        setStatusRef.current('Registrado');
        setHasActiveRtpRef.current(false);
        resetMuteState();
        stopRing();
      }
    } finally {
      joiningRef.current = false;
    }
  }, [apiCall, createWebRtcTransport, ensureDevice, consumeProducer, stopRing, resetMuteState]);

  const recoverActiveCall = useCallback(async () => {
    if (recoveryInFlightRef.current || recoveryAttemptedRef.current) return;
    const agentId = user?.agent || user?.username || user?.id || null;
    if (!agentId) return;
    recoveryAttemptedRef.current = true;
    recoveryInFlightRef.current = true;
    try {
      const snapshot = await apiCall(`/softphone/active-call/${encodeURIComponent(agentId)}`);
      if (!snapshot?.active || !snapshot.callId) {
        return;
      }

      const recoveredCallId = snapshot.callId;
      if (callIdRef.current && String(callIdRef.current) !== String(recoveredCallId)) {
        cleanupMedia();
      }
      if (callIdRef.current && String(callIdRef.current) === String(recoveredCallId)) {
        return;
      }

      resetMuteState();
      stopRing();
      setCallId(recoveredCallId);
      callIdRef.current = recoveredCallId;

      const actionId = snapshot.actionId || recoveredCallId;
      if (actionId && looksLikeActionId(actionId)) {
        actionIdRef.current = actionId;
      }
      if (snapshot.phone_number) {
        setCallerId(snapshot.phone_number);
      }

      setInCallRef.current(true);
      setStatusRef.current('En llamada');

      if (actionId || snapshot.skill || snapshot.data_id || snapshot.phone_number) {
        const baseCallMetadata = {
          actionId,
          skill: snapshot.skill || null,
          data_id: snapshot.data_id || null,
          callNumber: snapshot.phone_number || '',
          phone_number: snapshot.phone_number || '',
          idlog_calls: snapshot.idlog_calls || null,
          idload: snapshot.idload || null,
          source: 'recover',
          timestamp: new Date().toISOString()
        };
        setCallDataRef.current(baseCallMetadata);
        setTypificationDataRef.current({
          skillName: snapshot.skill || null,
          dataId: snapshot.data_id || null,
          actionId,
          callNumber: snapshot.phone_number || '',
          timestamp: new Date().toISOString(),
          source: 'recover'
        });
      }

      if (!sendTransportRef.current && !recvTransportRef.current) {
        joinAttemptRef.current = 0;
        consumedRef.current = new Set();
        await joinSfu(recoveredCallId);
      }

      if (snapshot.skill && snapshot.data_id && !skillDataFetchedRef.current) {
        try {
          const skillData = await apiCall(`/softphone/skill-data/${encodeURIComponent(snapshot.skill)}/${encodeURIComponent(snapshot.data_id)}`);
          skillDataFetchedRef.current = true;
          setCallDataRef.current({
            actionId,
            skill: snapshot.skill,
            data_id: snapshot.data_id,
            callNumber: snapshot.phone_number || '',
            phone_number: snapshot.phone_number || '',
            idlog_calls: snapshot.idlog_calls || null,
            idload: snapshot.idload || null,
            skillData: skillData || null,
            source: 'recover',
            timestamp: new Date().toISOString()
          });
        } catch (err) {
          console.warn('[SOFTPHONE] Failed to fetch skillData during recovery:', err?.message);
          if (actionId) {
            await fetchCallMetadata(actionId);
          }
        }
      } else if (actionId) {
        await fetchCallMetadata(actionId);
      }
    } catch (err) {
      console.warn('[SOFTPHONE] Failed to recover active call:', err?.message || err);
    } finally {
      recoveryInFlightRef.current = false;
    }
  }, [apiCall, user, cleanupMedia, fetchCallMetadata, joinSfu, looksLikeActionId, resetMuteState, stopRing]);

  // ── Multi-call media management ──

  const holdCallMedia = useCallback((holdCallId) => {
    const media = callMediaRef.current.get(holdCallId);
    if (!media) {
      console.warn('[SOFTPHONE][MULTI] holdCallMedia: no media for', holdCallId);
      return;
    }
    // Pause mediasoup-client producer (stop sending mic audio to this room)
    if (media.producer && !media.producer.paused) {
      try { media.producer.pause(); } catch (e) { console.warn('[SOFTPHONE][MULTI] pause producer err', e); }
    }
    // Clear single refs if this was the active call
    if (callIdRef.current && String(callIdRef.current) === String(holdCallId)) {
      sendTransportRef.current = null;
      recvTransportRef.current = null;
      producerRef.current = null;
      consumedRef.current = new Set();
      if (remoteAudioRef.current) {
        remoteAudioRef.current.srcObject = null;
      }
    }
    // Update context state
    holdCallState(holdCallId);
    // Emit to backend (MOH + MediaBridge pause)
    socketRef.current?.emit('call:hold', { callId: holdCallId });
    console.log('[SOFTPHONE][MULTI] Call held:', holdCallId);
  }, [holdCallState]);

  const resumeCallMedia = useCallback((resumeCallId) => {
    const media = callMediaRef.current.get(resumeCallId);
    if (!media) {
      console.warn('[SOFTPHONE][MULTI] resumeCallMedia: no media for', resumeCallId);
      return;
    }
    // Resume mediasoup-client producer
    if (media.producer && media.producer.paused) {
      try { media.producer.resume(); } catch (e) { console.warn('[SOFTPHONE][MULTI] resume producer err', e); }
    }
    // Restore single refs to this call
    sendTransportRef.current = media.sendTransport;
    recvTransportRef.current = media.recvTransport;
    producerRef.current = media.producer;
    consumedRef.current = media.consumed || new Set();
    callIdRef.current = resumeCallId;
    setCallId(resumeCallId);
    // Restore remote audio from consumers
    if (media.consumers && media.consumers.length > 0 && remoteAudioRef.current) {
      const stream = new MediaStream();
      media.consumers.forEach(c => {
        if (c.track && c.track.readyState === 'live') {
          stream.addTrack(c.track);
        }
      });
      if (stream.getAudioTracks().length > 0) {
        remoteAudioRef.current.srcObject = stream;
        remoteAudioRef.current.play().catch(() => {});
      }
    }
    // Update context state
    unholdCallState(resumeCallId);
    setHasActiveRtpRef.current(true);
    // Emit to backend (stop MOH + MediaBridge resume)
    socketRef.current?.emit('call:unhold', { callId: resumeCallId });
    console.log('[SOFTPHONE][MULTI] Call resumed:', resumeCallId);
  }, [unholdCallState]);

  const switchCallMedia = useCallback((fromCallId, toCallId) => {
    console.log('[SOFTPHONE][MULTI] Switching from', fromCallId, 'to', toCallId);
    // Hold current call locally (pause producer, clear refs)
    const fromMedia = callMediaRef.current.get(fromCallId);
    if (fromMedia?.producer && !fromMedia.producer.paused) {
      try { fromMedia.producer.pause(); } catch (e) {}
    }
    if (callIdRef.current && String(callIdRef.current) === String(fromCallId)) {
      sendTransportRef.current = null;
      recvTransportRef.current = null;
      producerRef.current = null;
      consumedRef.current = new Set();
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
    }
    // Resume target call locally
    const toMedia = callMediaRef.current.get(toCallId);
    if (toMedia) {
      if (toMedia.producer && toMedia.producer.paused) {
        try { toMedia.producer.resume(); } catch (e) {}
      }
      sendTransportRef.current = toMedia.sendTransport;
      recvTransportRef.current = toMedia.recvTransport;
      producerRef.current = toMedia.producer;
      consumedRef.current = toMedia.consumed || new Set();
      if (toMedia.consumers && toMedia.consumers.length > 0 && remoteAudioRef.current) {
        const stream = new MediaStream();
        toMedia.consumers.forEach(c => {
          if (c.track && c.track.readyState === 'live') stream.addTrack(c.track);
        });
        if (stream.getAudioTracks().length > 0) {
          remoteAudioRef.current.srcObject = stream;
          remoteAudioRef.current.play().catch(() => {});
        }
      }
    }
    callIdRef.current = toCallId;
    setCallId(toCallId);
    // Update context: hold from, unhold to
    holdCallState(fromCallId);
    unholdCallState(toCallId);
    setCurrentCall(toCallId);
    setHasActiveRtpRef.current(true);
    // Emit atomic switch to backend
    socketRef.current?.emit('call:switch', { fromCallId, toCallId });
    console.log('[SOFTPHONE][MULTI] Switched to', toCallId);
  }, [holdCallState, unholdCallState, setCurrentCall]);

  const mergeCallMedia = useCallback((primaryCallId, secondaryCallId) => {
    console.log('[SOFTPHONE][MULTI] Merging', secondaryCallId, 'into', primaryCallId);

    // Get secondary call's phone number BEFORE merging (it gets removed from calls state)
    const secCallInfo = calls?.[secondaryCallId];
    const secPhoneNumber = secCallInfo?.callData?.callNumber || secCallInfo?.callData?.phone_number || null;

    // Close secondary call's mediasoup-client transports
    const secMedia = callMediaRef.current.get(secondaryCallId);
    if (secMedia) {
      try { secMedia.clonedTrack?.stop(); } catch {}
      try { secMedia.producer?.close(); } catch {}
      if (secMedia.consumers) {
        secMedia.consumers.forEach(c => { try { c.close(); } catch {} });
      }
      try { secMedia.sendTransport?.close(); } catch {}
      try { secMedia.recvTransport?.close(); } catch {}
      callMediaRef.current.delete(secondaryCallId);
    }
    // Update context
    mergeCallsState(primaryCallId, secondaryCallId);

    // Track merged call as conference participant
    if (addConferenceParticipant) {
      addConferenceParticipant(secondaryCallId, secPhoneNumber);
    }

    // Make sure primary is the active call
    if (String(callIdRef.current) !== String(primaryCallId)) {
      const primMedia = callMediaRef.current.get(primaryCallId);
      if (primMedia) {
        sendTransportRef.current = primMedia.sendTransport;
        recvTransportRef.current = primMedia.recvTransport;
        producerRef.current = primMedia.producer;
        consumedRef.current = primMedia.consumed || new Set();
      }
      callIdRef.current = primaryCallId;
      setCallId(primaryCallId);
    }
    // Emit to backend (moves SIP channel to primary bridge)
    socketRef.current?.emit('call:merge', { primaryCallId, secondaryCallId });
    console.log('[SOFTPHONE][MULTI] Merged into', primaryCallId, 'phone:', secPhoneNumber);
  }, [mergeCallsState, calls, addConferenceParticipant]);

  const hangupConferenceParticipantFn = useCallback((participantCallId) => {
    const primaryCallId = callIdRef.current;
    if (!primaryCallId || !participantCallId) return;
    console.log('[SOFTPHONE] Hanging up conference participant', { primaryCallId, participantCallId });
    socketRef.current?.emit('call:hangup-participant', { callId: primaryCallId, participantCallId });
  }, []);

  const cleanupCallMedia = useCallback((cleanCallId) => {
    const media = callMediaRef.current.get(cleanCallId);
    if (!media) return;
    try { media.clonedTrack?.stop(); } catch {}
    try { media.producer?.close(); } catch {}
    if (media.consumers) {
      media.consumers.forEach(c => { try { c.close(); } catch {} });
    }
    try { media.sendTransport?.close(); } catch {}
    try { media.recvTransport?.close(); } catch {}
    callMediaRef.current.delete(cleanCallId);
    // If this was active, clear single refs
    if (callIdRef.current && String(callIdRef.current) === String(cleanCallId)) {
      sendTransportRef.current = null;
      recvTransportRef.current = null;
      producerRef.current = null;
      consumedRef.current = new Set();
    }
    // Update context
    removeCall(cleanCallId);
    // If no more calls, reset state
    if (callMediaRef.current.size === 0) {
      callIdRef.current = null;
      setCallId(null);
      setInCallRef.current(false);
      setHasActiveRtpRef.current(false);
      setStatusRef.current('Registrado');
      if (remoteAudioRef.current) remoteAudioRef.current.srcObject = null;
      stopRing();
      resetMuteState();
      playCallEndBeep();
    } else {
      // Promote next call as active if this was the active one
      if (!callIdRef.current || String(callIdRef.current) === String(cleanCallId)) {
        const nextCallId = callMediaRef.current.keys().next().value;
        if (nextCallId) {
          resumeCallMedia(nextCallId);
        }
      }
      playCallEndBeep();
    }
    console.log('[SOFTPHONE][MULTI] Cleaned up media for', cleanCallId);
  }, [removeCall, resumeCallMedia, stopRing, resetMuteState, playCallEndBeep]);

  const connectSocket = useCallback(() => {
    if (!tokenRef.current) return;
    if (socketRef.current?.connected) return;
    if (socketRef.current && !socketRef.current.connected) {
      socketRef.current.auth = { token: tokenRef.current };
      socketRef.current.connect();
      return;
    }

    const existing = typeof window !== 'undefined' ? window.__ctiSfuSocket : null;
    if (existing) {
      socketRef.current = existing;
      if (!existing.connected) {
        existing.auth = { token: tokenRef.current };
        existing.connect();
      }
      return;
    }

    const socket = io(wsBaseUrl, {
      path: '/socket.io',
      transports: ['polling', 'websocket'],
      reconnection: true,
      reconnectionAttempts: Infinity,
      reconnectionDelay: 1000,
      reconnectionDelayMax: 10000,
      timeout: 15000,
      auth: { token: tokenRef.current }
    });
    socketRef.current = socket;
    if (typeof window !== 'undefined') {
      window.__ctiSfuSocket = socket;
    }

    socket.on('connect', () => {
      console.log('[SOFTPHONE] Socket connected — id:', socket.id);
      setConnected(true);
      setStatusRef.current('Conectado');
      setRegisteredRef.current(true);
      connectedOnceRef.current = true;
      recoverActiveCall();
      if (reconnectTimerRef.current) {
        clearTimeout(reconnectTimerRef.current);
        reconnectTimerRef.current = null;
      }
    });

    socket.on('disconnect', (reason) => {
      console.log('[SOFTPHONE] Socket disconnected — reason:', reason);
      setConnected(false);
      setStatusRef.current('Desconectado');
      setRegisteredRef.current(false);
      // Permitir re-recuperación de llamada activa al reconectar
      recoveryAttemptedRef.current = false;
      recoveryInFlightRef.current = false;
      // Reconexión manual como backup — necesario para "io server disconnect"
      // (socket.io NO reconecta automáticamente en ese caso) y como red de
      // seguridad para cualquier otro motivo
      if (!reconnectTimerRef.current) {
        reconnectTimerRef.current = setTimeout(() => {
          reconnectTimerRef.current = null;
          if (socketRef.current && !socketRef.current.connected) {
            console.log('[SOFTPHONE] Manual reconnect attempt (reason was:', reason, ')');
            socketRef.current.auth = { token: tokenRef.current };
            socketRef.current.connect();
          }
        }, 2000);
      }
    });

    socket.on('connect_error', (err) => {
      const errMsg = err?.message || '';
      console.warn('[SOFTPHONE] WS connect_error', errMsg);
      // Solo actualizar estado si realmente no estamos conectados
      if (!socket.connected) {
        setConnected(false);
        setStatusRef.current('Desconectado');
        setRegisteredRef.current(false);
      }

      // Token expired or invalid: stop reconnecting, force re-login
      if (errMsg === 'Token expired' || errMsg === 'Invalid token') {
        console.error('[SOFTPHONE] Auth token expired/invalid — stopping reconnection');
        socket.disconnect();
        if (typeof window !== 'undefined') window.__ctiSfuSocket = null;
        socketRef.current = null;
        localStorage.removeItem('cti_admin_token');
        localStorage.removeItem('cti_admin_user');
        window.location.reload();
        return;
      }
    });


    socket.on('call.state', (payload) => {
      console.log('[SOFTPHONE][DBG] call.state', {
        call_id: payload.call_id || payload.callId || null,
        ari_call_id: payload.ari_call_id || payload.ariCallId || null,
        actionId: payload.actionId || payload.action_id || payload.actionid || null,
        uniqueid: payload.uniqueid || payload.uniqueId || null,
        state: payload.state
      });
      const id = payload.call_id || payload.callId || null;
      const ariCallId = payload.ari_call_id || payload.ariCallId || null;
      const actionId = payload.actionId || payload.action_id || payload.actionid || payload.call_id || payload.callId || null;
      const uniqueId = payload.uniqueid || payload.uniqueId || null;

      // Always populate uniqueIdMap (needed by call:incoming fallback)
      if (uniqueId && ariCallId) {
        uniqueIdMapRef.current.set(String(uniqueId), String(ariCallId));
        // Resolve pending uniqueId from call:incoming that arrived without ari_call_id
        if (pendingUniqueIdRef.current && String(pendingUniqueIdRef.current) === String(uniqueId)) {
          pendingUniqueIdRef.current = null;
          if (!callIdRef.current) {
            setCallId(ariCallId);
            callIdRef.current = ariCallId;
            // Now we have the correct callId, join SFU
            joinAttemptRef.current = 0;
            consumedRef.current = new Set();
            joinSfu(ariCallId);
          }
        }
      }

      // Don't set callIdRef from broadcast events.
      // Only call:incoming (targeted per-agent) should set callIdRef.
      if (!callIdRef.current && callMediaRef.current.size === 0) return;

      const effectiveId = ariCallId || actionId || id;
      const isActiveCall = !effectiveId || String(callIdRef.current) === String(effectiveId);
      const isHeldCall = !isActiveCall && callMediaRef.current.has(effectiveId);

      // Ignore events for calls we don't own
      if (!isActiveCall && !isHeldCall) {
        return;
      }

      // Only update active call UI state (callerId, status) for the active call
      if (isActiveCall) {
        if (actionId && looksLikeActionId(actionId)) {
          actionIdRef.current = actionId;
        }

        const num = payload.callerid?.num || payload.calleridnum || '';
        if (num) setCallerId(num);

        const activeCallStates = ['Ringing', 'Ring', 'Up', 'up', 'answered', 'stasis'];
        if (activeCallStates.includes(payload.state)) {
          setInCallRef.current(true);
          setStatusRef.current('En llamada');
        }
      }

      if (payload.state === 'ended') {
        // Multi-call: clean up only the specific call that ended
        if ((isHeldCall || callMediaRef.current.size > 1) && effectiveId) {
          cleanupCallMedia(effectiveId);
        } else {
          cleanupMedia();
          setInCallRef.current(false);
        }
      }
    });

    socket.on('call.media.ready', async (payload) => {
      console.log('[SOFTPHONE][DBG] call.media.ready', {
        ari_call_id: payload.ari_call_id || payload.ariCallId || null,
        call_id: payload.call_id || payload.callId || null
      });
      // Only join SFU if call:incoming has already set callIdRef
      if (!callIdRef.current) return;

      const rawId = payload.ari_call_id || payload.ariCallId || payload.call_id || payload.callId;
      const idStr = rawId != null ? String(rawId) : '';
      const looksLikeUniqueIdValue = looksLikeUniqueId(idStr);
      const effectiveId = looksLikeUniqueIdValue ? (actionIdRef.current || rawId) : rawId;

      // Only process if this event matches our active call
      if (!effectiveId || String(callIdRef.current) !== String(effectiveId)) return;

      // If already joined (transports exist), don't rejoin
      if (sendTransportRef.current || recvTransportRef.current) return;

      joinAttemptRef.current = 0;
      consumedRef.current = new Set();
      await joinSfu(effectiveId);
      setInCallRef.current(true);
      setStatusRef.current('En llamada');
    });

    // Inbound call metadata from backend (AgentConnect → queueCallConnected)
    // This is the TARGETED event (sent only to the assigned agent).
    // It is the authoritative source for which call the agent should handle.
    socket.on('call:incoming', async (payload) => {
      console.log('[SOFTPHONE] call:incoming received:', payload);
      console.log('[SOFTPHONE][DBG] call:incoming ids', {
        actionId: payload.actionId || payload.action_id || payload.call_id || null,
        ariCallId: payload.ari_call_id || payload.ariCallId || null,
        uniqueId: payload.uniqueid || payload.uniqueId || null,
        dataId: payload.data_id || payload.dataId || null,
        skill: payload.skill || null,
        agent: payload.agent || payload.agentId || payload.username || null
      });
      resetMuteState();
      stopRing();
      playIncomingBeep();
      const actionId = payload.actionId || payload.action_id || null;
      const ariCallId = payload.ari_call_id || payload.ariCallId || null;
      const skill = payload.skill || null;
      const dataId = payload.data_id || payload.dataId || null;
      const phoneNumber = payload.phone_number || payload.callNumber || '';
      const uniqueId = payload.uniqueid || payload.uniqueId || null;

      // Determine the best callId (SFU room key) to use
      const bestCallId = ariCallId || (uniqueId && uniqueIdMapRef.current.get(String(uniqueId))) || actionId;

      if (!bestCallId) {
        // No resolvable callId yet; store pending for resolution via call.state
        if (uniqueId) pendingUniqueIdRef.current = uniqueId;
        console.warn('[SOFTPHONE][DBG] call:incoming missing bestCallId, pending uniqueId', { uniqueId });
        return;
      }

      // If we already have a different active call, HOLD it for multi-call
      if (callIdRef.current && String(callIdRef.current) !== String(bestCallId)) {
        if (callMediaRef.current.has(callIdRef.current)) {
          holdCallMedia(callIdRef.current);
        } else {
          cleanupMedia();
        }
      }

      // Set the authoritative callId
      setCallId(bestCallId);
      callIdRef.current = bestCallId;

      console.log('[SOFTPHONE][DBG] callId set', { bestCallId, actionId, ariCallId });

      if (actionId && looksLikeActionId(actionId)) {
        actionIdRef.current = actionId;
      }
      if (phoneNumber) setCallerId(phoneNumber);

      // Set minimal call metadata immediately so typification isn't lost if call ends fast
      if (actionId || skill || dataId || phoneNumber) {
        const baseCallMetadata = {
          actionId,
          skill,
          data_id: dataId,
          callNumber: phoneNumber,
          phone_number: phoneNumber,
          idlog_calls: payload.idlog_calls,
          idload: payload.idload,
          source: 'predictive',
          timestamp: new Date().toISOString()
        };
        setCallDataRef.current(baseCallMetadata);
        setTypificationDataRef.current({
          skillName: skill,
          dataId,
          actionId,
          callNumber: phoneNumber,
          timestamp: new Date().toISOString(),
          source: 'incoming'
        });
      }

      // Register in multi-call state
      addCall(bestCallId, {
        actionId,
        skill,
        data_id: dataId,
        callNumber: phoneNumber,
        phone_number: phoneNumber,
        source: 'predictive',
        timestamp: new Date().toISOString()
      });

      // Agent is connected to a call (AgentConnect fired)
      setInCallRef.current(true);
      setStatusRef.current('En llamada');

      // Join the SFU room for this call (created earlier by attachToChannelByName)
      if (!sendTransportRef.current && !recvTransportRef.current) {
        joinAttemptRef.current = 0;
        consumedRef.current = new Set();
        await joinSfu(bestCallId);
      }

      // Fetch skillData from the backend
      if (skill && dataId && !skillDataFetchedRef.current) {
        console.log('[SOFTPHONE][DBG] skillData fetch start', { skill, dataId, actionId, bestCallId });
        try {
          const skillData = await apiCall(`/softphone/skill-data/${encodeURIComponent(skill)}/${encodeURIComponent(dataId)}`);
          skillDataFetchedRef.current = true;
          console.log('[SOFTPHONE][DBG] skillData fetch success', { skill, dataId, actionId, hasSkillData: !!skillData });

          const callMetadata = {
            actionId,
            skill,
            data_id: dataId,
            callNumber: phoneNumber,
            phone_number: phoneNumber,
            idlog_calls: payload.idlog_calls,
            idload: payload.idload,
            skillData: skillData || null,
            source: 'predictive',
            timestamp: new Date().toISOString()
          };
          console.log('[SOFTPHONE] call:incoming metadata set:', callMetadata);
          setCallDataRef.current(callMetadata);

          setTypificationDataRef.current({
            skillName: skill,
            dataId,
            actionId,
            callNumber: phoneNumber,
            timestamp: new Date().toISOString(),
            source: 'incoming'
          });
        } catch (err) {
          console.warn('[SOFTPHONE] Failed to fetch skillData on call:incoming:', err?.message);
          console.warn('[SOFTPHONE][DBG] skillData fetch error', { skill, dataId, actionId, bestCallId, error: err?.message || String(err) });
          scheduleSkillDataRetry({ callId: bestCallId, skill, dataId, actionId, phoneNumber, payload });
        }
      } else if (actionId) {
        // If no skill/dataId in the event, try fetching full metadata by actionId/uniqueid
        console.log('[SOFTPHONE][DBG] fetchCallMetadata fallback', { actionId, bestCallId, skill, dataId });
        await fetchCallMetadata(actionId);
      } else {
        console.warn('[SOFTPHONE][DBG] No skill/dataId and no actionId to fetch metadata', {
          bestCallId,
          skill,
          dataId
        });
      }

      if (skill && dataId && skillDataFetchedRef.current) {
        console.warn('[SOFTPHONE][DBG] skillData fetch skipped because already fetched', {
          actionId,
          bestCallId,
          skill,
          dataId
        });
      }
    });

    socket.on('call:completed', () => {
      resetMuteState();
      stopRing();
      playCallEndBeep();
    });

    socket.on('call:ended', () => {
      resetMuteState();
      stopRing();
      playCallEndBeep();
    });

    // Direct conference add: a new participant was added to the current call's bridge
    socket.on('call.conference.added', (payload) => {
      const targetCallId = payload.call_id || payload.callId;
      console.log('[SOFTPHONE] call.conference.added received:', payload);
      if (targetCallId && String(targetCallId) === String(callIdRef.current)) {
        // Track the new conference participant
        if (addConferenceParticipant) {
          addConferenceParticipant(payload.added_call_id, payload.phone_number || null);
        }
        // Update call state to conference
        if (mergeCallsState) {
          mergeCallsState(targetCallId, payload.added_call_id);
        }
        console.log('[SOFTPHONE] Conference participant added to current call', {
          callId: targetCallId,
          addedCallId: payload.added_call_id,
          phoneNumber: payload.phone_number,
          totalParticipants: payload.total_participants
        });
      }
    });

    // Conference participant removed (hung up)
    socket.on('call.conference.removed', (payload) => {
      const targetCallId = payload.call_id || payload.callId;
      console.log('[SOFTPHONE] call.conference.removed received:', payload);
      if (targetCallId && String(targetCallId) === String(callIdRef.current)) {
        // Remove the conference participant from tracking
        if (removeConferenceParticipant && payload.removed_call_id) {
          removeConferenceParticipant(payload.removed_call_id);
        }
        // If no more merged participants, revert from conference to active
        if (payload.remaining_participants <= 1 && calls && calls[targetCallId]) {
          addCall(targetCallId, { ...calls[targetCallId].callData, state: 'active' });
        }
        console.log('[SOFTPHONE] Conference participant removed', {
          callId: targetCallId,
          removedCallId: payload.removed_call_id,
          remaining: payload.remaining_participants
        });
      }
    });

    socket.on('call.media.producer', async (payload) => {
      // Only consume producers after call:incoming has set callIdRef
      if (!callIdRef.current) return;

      const rawId = payload.ari_call_id || payload.ariCallId || payload.call_id || payload.callId;
      const idStr = rawId != null ? String(rawId) : '';
      const looksLikeUniqueIdValue = looksLikeUniqueId(idStr);
      const id = looksLikeUniqueIdValue ? (actionIdRef.current || rawId) : rawId;
      const producerId = payload.producer_id || payload.producerId;

      if (!id || !producerId) return;
      if (String(callIdRef.current) !== String(id)) return;

      await consumeProducer(id, producerId);
    });
  }, [joinSfu, consumeProducer, cleanupMedia, cleanupCallMedia, holdCallMedia, fetchCallMetadata, apiCall, wsBaseUrl, looksLikeActionId, looksLikeUniqueId, resetMuteState, stopRing, playIncomingBeep, addCall]);

  useEffect(() => {
    if (!token) return;
    connectSocket();
    return () => {
      // Do not auto-disconnect to avoid reconnect storms in StrictMode
    };
  }, [connectSocket, token]);

  // Chequeo periódico: sincronizar estado React con estado real del socket
  useEffect(() => {
    const interval = setInterval(() => {
      const sock = socketRef.current;
      if (sock && sock.connected) {
        setConnected((prev) => {
          if (!prev) {
            console.log('[SOFTPHONE] State sync — socket connected but React state was false, correcting');
            setStatusRef.current('Conectado');
            setRegisteredRef.current(true);
            connectedOnceRef.current = true;
          }
          return true;
        });
      }
    }, 3000);
    return () => clearInterval(interval);
  }, []);

  // Detectar cambios de red y suspensión del sistema para forzar reconexión
  useEffect(() => {
    const handleOnline = () => {
      console.log('[SOFTPHONE] Browser online — forcing reconnection check');
      if (socketRef.current && !socketRef.current.connected) {
        socketRef.current.auth = { token: tokenRef.current };
        socketRef.current.connect();
      }
    };

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        console.log('[SOFTPHONE] Page visible — checking socket connection');
        const sock = socketRef.current;
        if (sock && !sock.connected) {
          console.log('[SOFTPHONE] Socket disconnected after wake/focus — reconnecting');
          sock.auth = { token: tokenRef.current };
          sock.connect();
        } else if (sock && sock.connected) {
          // Sincronizar estado React si el socket ya está conectado pero el estado quedó desincronizado
          setConnected(true);
          setStatusRef.current('Conectado');
          setRegisteredRef.current(true);
        }
      }
    };

    window.addEventListener('online', handleOnline);
    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => {
      window.removeEventListener('online', handleOnline);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, []);

  const answer = useCallback(() => {
    const id = callIdRef.current;
    if (!socketRef.current || !id) return;
    stopRing();
    socketRef.current.emit('call:answer', { callId: id });
  }, [looksLikeActionId, stopRing]);

  const hangup = useCallback((specificCallId) => {
    // Guard: React onClick passes SyntheticEvent as first arg — reject non-string values
    const id = (typeof specificCallId === 'string' && specificCallId) ? specificCallId : callIdRef.current;
    const actionId = actionIdRef.current;
    if (!socketRef.current) return;
    stopRing();
    // Notify backend to hang up the ARI channel + cleanup SFU
    if (id) {
      socketRef.current.emit('call:hangup', { callId: id });
    } else if (looksLikeActionId(actionId)) {
      socketRef.current.emit('call:hangup', { actionId });
    }
    // Also flag the call as hung up in the call log (like old Softphone)
    if (looksLikeActionId(actionId)) {
      fetch('/api/admin/calls/hangupcall', {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${tokenRef.current}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ actionId, hangupcall: 1 })
      }).catch(() => {});
    }
    // LOCAL CLEANUP: always clean frontend state, don't rely solely on backend response.
    // This prevents getting stuck when backend can't find the call (pipeline failed, etc.)
    const cleanId = id || (looksLikeActionId(actionId) ? actionId : null);
    // Count total calls tracked in context (includes calls without media, e.g. stuck ringing)
    const totalContextCalls = calls ? Object.keys(calls).length : 0;
    const isMultiCall = totalContextCalls > 1 || callMediaRef.current.size > 1;

    if (cleanId && isMultiCall) {
      // Multi-call: clean only the specific call
      if (callMediaRef.current.has(cleanId)) {
        cleanupCallMedia(cleanId);
      } else {
        // Call had no media (stuck ringing) - remove from context + promote next
        removeCall(cleanId);
        if (String(callIdRef.current) === String(cleanId)) {
          const nextCallId = callMediaRef.current.keys().next().value;
          if (nextCallId) {
            callIdRef.current = nextCallId;
            setCallId(nextCallId);
            resumeCallMedia(nextCallId);
          } else {
            // No more calls with media
            callIdRef.current = null;
            setCallId(null);
            setInCallRef.current(false);
            setStatusRef.current('Registrado');
          }
        }
      }
    } else {
      // Single call or no calls: full cleanup
      cleanupMedia();
      setInCallRef.current(false);
      setStatusRef.current('Registrado');
    }
  }, [stopRing, cleanupCallMedia, cleanupMedia, removeCall, resumeCallMedia, calls]);

  const toggleMute = useCallback(() => {
    const producer = producerRef.current;
    if (!producer) return;
    mutedRef.current = !mutedRef.current;
    const maybePromise = mutedRef.current ? producer.pause() : producer.resume();
    if (maybePromise && typeof maybePromise.catch === 'function') {
      maybePromise.catch(() => {});
    }
    setContextMutedRef.current(mutedRef.current);
  }, []);

  useEffect(() => {
    if (functionsSetRef.current) return;
    setSoftphoneFunctions({
      answer,
      hangup,
      toggleMute,
      holdCall: holdCallMedia,
      resumeCall: resumeCallMedia,
      switchCall: switchCallMedia,
      mergeCall: mergeCallMedia,
      hangupConferenceParticipant: hangupConferenceParticipantFn
    });
    functionsSetRef.current = true;
  }, [setSoftphoneFunctions, answer, hangup, toggleMute, holdCallMedia, resumeCallMedia, switchCallMedia, mergeCallMedia, hangupConferenceParticipantFn]);

  return (
    <Box sx={{ display: 'none' }}>
      <audio ref={remoteAudioRef} autoPlay />
      <Card>
        <CardContent>
          <Typography variant="h6">Softphone WebRTC</Typography>
          <Typography variant="body2">Estado: {connected ? 'Conectado' : 'Desconectado'}</Typography>
          <Typography variant="body2">Caller: {callerId || 'N/A'}</Typography>
          <Button onClick={answer} disabled={!callId}>Contestar</Button>
          <Button onClick={hangup} disabled={!callId && !looksLikeActionId(actionIdRef.current)}>Colgar</Button>
        </CardContent>
      </Card>
    </Box>
  );
}
